import { z } from 'zod'

import { toWallClockDate } from '../lib/date'
import { clinicIdSchema } from './clinic'

/** The seven day names, in the order the grid renders them. */
export const DAY_KEYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const
export type DayKey = (typeof DAY_KEYS)[number]

/** Length of the recurring rotation cycle, in weeks. */
export const ROTATION_CYCLE_LENGTH_WEEKS = 4

/** A single assigned cell: which clinic + which template. */
export const rotationCellSchema = z.object({
  clinicId: clinicIdSchema,
  /** Schedule template id; validated server-side against schedule_templates. */
  templateId: z.uuid(),
})
export type RotationCell = z.infer<typeof rotationCellSchema>

/** One week: every day mapped to a cell or null (off). */
export const rotationWeekSchema = z.object(
  Object.fromEntries(DAY_KEYS.map((d) => [d, rotationCellSchema.nullable()])) as Record<
    DayKey,
    z.ZodNullable<typeof rotationCellSchema>
  >
)
export type RotationWeek = Record<DayKey, RotationCell | null>

/** The full cycle for one staff member, one entry per week in order (index = getWeekIndex). */
export const rotationGridSchema = z.array(rotationWeekSchema).length(ROTATION_CYCLE_LENGTH_WEEKS)
export type RotationGrid = RotationWeek[]

/** Monday April 27 2026 — Monday of the week containing May 1 2026 (week 1 of the rotation). */
export const SCHEDULE_ANCHOR = new Date('2026-04-27T00:00:00.000Z')

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000

/**
 * Whole weeks between the schedule anchor and `date` (negative for dates before the
 * anchor). Resolves the local YYYY-MM-DD and re-parses as UTC midnight so the week
 * arithmetic lines up with the UTC-midnight anchor — avoiding an off-by-one when the
 * UTC instant lands on a different day than the wall-clock date (e.g. midnight BST =
 * 23:00 UTC the previous day). Single source of the anchor math for the helpers below.
 */
function weeksSinceAnchor(date: Date, timezone: string): number {
  const effectiveDate = new Date(`${toWallClockDate(date, timezone)}T00:00:00.000Z`)
  return Math.floor((effectiveDate.getTime() - SCHEDULE_ANCHOR.getTime()) / MS_PER_WEEK)
}

/**
 * Returns 0–3 indicating which week in the 4-week rotation the date falls in.
 */
export function getWeekIndex(date: Date, timezone: string): number {
  // Double-modulo handles dates before the anchor without going negative.
  return ((weeksSinceAnchor(date, timezone) % 4) + 4) % 4
}

/**
 * Returns the UTC-midnight Monday that starts the rotation week containing `date`,
 * anchored the same way as getWeekIndex. Lets callers lay out a rotation cycle's
 * calendar dates without re-deriving the anchor arithmetic (and drifting from the
 * index the backend resolves).
 */
export function getRotationWeekStart(date: Date, timezone: string): Date {
  return new Date(SCHEDULE_ANCHOR.getTime() + weeksSinceAnchor(date, timezone) * MS_PER_WEEK)
}

/** Returns the human-facing rotation week number (1–4) the date falls in. */
export function getRotationWeek(date: Date, timezone: string): number {
  return getWeekIndex(date, timezone) + 1
}

/**
 * Builds a fresh all-off grid (every week/day mapped to null). Generic over the cell
 * type so it serves both the API grid (`RotationCell`) and the backend's in-memory
 * rotation cell. Returns a new array each call.
 */
export function blankWeekGrid<T>(): Record<DayKey, T | null>[] {
  return Array.from(
    { length: ROTATION_CYCLE_LENGTH_WEEKS },
    () => Object.fromEntries(DAY_KEYS.map((day) => [day, null])) as Record<DayKey, T | null>
  )
}
